/* eslint-disable no-unreachable */
/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable no-cond-assign */
import { useMutation } from "@apollo/client";
import { Cascader, Form, Select, Tabs, message } from "antd";
import _, { isEmpty } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { UserContext } from "../../amplify/authenticator-provider";
import { CREATE_PROMPT, UPDATE_PROMPT } from "../../graphql/mutation";
import { setDrawerLoading } from "../../library/drawerLoadingService";
import { closeDrawer, isAllLocationSelected } from "../../library/helpers";
import UserSelector from "../commonComponents/userSelector";
import { RichTextEditorJSX } from "./RichTextEditor";
import "./ottosettings.css";
import useLocation from "../../hooks/useLocation";
import { getSessionStorage } from "../../utils/storages";
import { getCompanyId } from "../../redux/slices/companySlice";

// Utility function to find cascader paths for selected source IDs
const findSourcePaths = (sourceList, selectedIds) => {
  const paths = [];

  const findPathRecursive = (nodes, targetId, currentPath = []) => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const newPath = [...currentPath, node.key];

      if (node.key === targetId) {
        paths.push(newPath);
        return true;
      }

      if (node.childs && node.childs.length > 0) {
        if (findPathRecursive(node.childs, targetId, newPath)) {
          return true;
        }
      }
    }
    return false;
  };

  selectedIds.forEach(id => {
    findPathRecursive(sourceList, id);
  });

  return paths;
};

export const OttoSettingsForm = ({
  form,
  teamList,
  roleList,
  refetchPromptList,
  setpromptList,
  settingsCompany,
  sourceList,
  mode: promptMode = "ADD",
  prompt = {},
  isAdmin,
}) => {
  const { user } = useContext(UserContext);
  const companyId = useSelector(getCompanyId);
  const { applicationUseCases, applicationLevels } = useSelector(state => state.aiData);
  const [formData, setformData] = useState({
    applicationLevelId: 1,
    promptText: "",
    leadSourceOriginalIds: [],
    formattext: "",
    userId: user.id,
  });

  // Fetch locations once at parent level if admin
  const { data: locationData, loading: locationsLoading } = useLocation({
    variables: { companyId: companyId },
    skip: !isAdmin || !companyId,
    fetchPolicy: "cache-first",
  });

  const locations = locationData?.getLocations || [];

  useEffect(() => {
    if (promptMode === "EDIT" && sourceList && sourceList.length > 0) {
      const selectedSourceIds = prompt.promptSources?.map(item => item.leadSourceOriginalId) || [];
      const sourcePaths = findSourcePaths(sourceList, selectedSourceIds);

      setformData({
        applicationLevelId: prompt.applicationLevelId,
        userId: prompt.userId,
        teamId: prompt.teamId,
        companyRoleId: prompt.companyRoleId,
        applicationUseCaseId: +prompt.applicationUseCaseId,
        levelCode: prompt.applicationLevel.code,
        locationId: prompt?.locationId?.toString(),
        leadSourceOriginalIds: sourcePaths,
        promptText: prompt.promptText,
      });
      form.setFieldsValue({
        applicationLevelId: prompt.applicationLevelId,
        userId: prompt.userId,
        teamId: prompt.teamId,
        companyRoleId: prompt.companyRoleId,
        applicationUseCaseId: +prompt.applicationUseCaseId,
        locationId: prompt?.locationId?.toString(),
        leadSourceOriginalIds: sourcePaths,
      });
    }
  }, [promptMode, prompt, form, sourceList]);

  const [createPrompt] = useMutation(CREATE_PROMPT, {
    onError: err => {
      message.error(err.message || "Something went wrong");
    },
  });

  const [updatePrompt] = useMutation(UPDATE_PROMPT, {
    onError: err => {
      message.error(err.message || "Something went wrong");
    },
  });

  const handleSave = async value => {
    const levelCode = formData.levelCode || "DEFAULT";

    // Define which fields to include for each level
    const levelFieldMap = {
      DEFAULT: ["companyId"],
      COMPANY: ["companyId"],
      ROLE: ["companyId", "locationId", "companyRoleId"],
      TEAM: ["companyId", "locationId", "teamId"],
      USER: ["companyId", "locationId", "userId"],
    };

    const allowedFields = levelFieldMap[levelCode] || Object.keys(formData);
    const basePayload = { ...value, ...formData, levelCode, companyId: settingsCompany.id };

    // Add locationId for levels that need it
    if (["ROLE", "TEAM", "USER"].includes(levelCode)) {
      basePayload.locationId = sessionStorage.getItem("currentLocationId");
    }

    // Filter payload to only include allowed fields
    const payload = Object.fromEntries(
      Object.entries(basePayload).filter(
        ([key]) =>
          allowedFields.includes(key) ||
          !["locationId", "teamId", "userId", "companyRoleId"].includes(key)
      )
    );

    if (isEmpty(payload.promptText)) {
      message.error("Prompt text is required");
      return false;
    }

    setDrawerLoading(true);
    try {
      if (promptMode === "ADD") {
        const res = await createPrompt({
          variables: {
            ...payload,
            leadSourceOriginalIds: _.map(payload.leadSourceOriginalIds, _.last),
          },
        });
        if (res.data.createPrompt.statusCode === 200) {
          message.success(res.data.createPrompt.message);
          const promptRes = await refetchPromptList();
          setpromptList(promptRes.data.getPrompts);
          closeDrawer();
          return true;
        }
        message.error(res.data.createPrompt.message);
        return false;
      }
      const res = await updatePrompt({
        variables: {
          id: prompt.id,
          ...payload,
          leadSourceOriginalIds: _.map(payload.leadSourceOriginalIds, _.last),
        },
      });
      if (res.data.updatePrompt.statusCode === 200) {
        message.success(res.data.updatePrompt.message);
        const promptRes = await refetchPromptList();
        setpromptList(promptRes.data.getPrompts);
        closeDrawer();
        return true;
      }
      message.error(res.data.updatePrompt.message);
      return false;
    } catch (error) {
      console.error("Error saving prompt:", error);
      message.error("An error occurred while saving the prompt");
      return false;
    } finally {
      setDrawerLoading(false);
    }
  };

  const RenderCustomSelect = ({ code }) => {
    switch (code?.toLowerCase()) {
      case "company":
        return null;
      case "user":
        return (
          <UserSelector
            item={{ name: <span className="form-field-label">User</span> }}
            containerClassName="flex-1"
            name="userId"
            showAll={false}
            errorMessage="User is required"
            allowClear
            placeholder="Select users"
            disabled={promptMode === "EDIT"}
            multiple={false}
            validation
            value={formData.userId}
            handleUsersSelection={value => setformData({ ...formData, userId: value })}
            extraQueryParams={
              isAdmin
                ? {
                    allUser: true,
                    isCurrentLocationUser: false,
                    isLocationFilter: false,
                  }
                : {
                    locationId: isAllLocationSelected()
                      ? getSessionStorage("currentLocationId")
                          ?.split(",")
                          ?.map(id => +id)
                      : getSessionStorage("currentLocationId"),
                    isCurrentLocationUser: true,
                  }
            }
          />
        );
      case "role":
        return (
          <Form.Item
            label={<span className="form-field-label">Role</span>}
            name="companyRoleId"
            className="flex-1"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select
              placeholder="Select Role"
              name="companyRoleId"
              disabled={promptMode === "EDIT"}
              className="w-full"
              onSelect={value => setformData({ ...formData, companyRoleId: +value })}
            >
              {(roleList || []).map(item => (
                <Select.Option key={item.id} value={+item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );
      case "team":
        return (
          <Form.Item
            label={<span className="form-field-label">Team</span>}
            name="teamId"
            className="flex-1"
            rules={[{ required: true, message: "Team is required" }]}
          >
            <Select
              placeholder="Select Team"
              name="teamId"
              disabled={promptMode === "EDIT"}
              className="w-full"
              onSelect={value => setformData({ ...formData, teamId: +value })}
            >
              {(teamList || []).map(item => (
                <Select.Option key={item.id} value={+item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );
      case "location":
        return (
          <Form.Item
            label={<span className="form-field-label">Location</span>}
            className="flex-1"
            name="locationId"
            rules={[{ required: true, message: "Location is required" }]}
          >
            <Select
              showSearch
              placeholder="Select location"
              disabled={promptMode === "EDIT"}
              className="w-full"
              loading={isAdmin && locationsLoading}
              allowClear
              onChange={value => setformData({ ...formData, locationId: value })}
              optionFilterProp="children"
            >
              {isAdmin
                ? locations?.map(loc => (
                    <Select.Option key={String(loc.id)} value={String(loc.id)}>
                      {loc.locationType ? `${loc.locationType}: ` : ""}
                      {loc.title}
                    </Select.Option>
                  ))
                : (user.locations || []).map(item => (
                    <Select.Option key={String(item.id)} value={String(item.location.id)}>
                      {item.location.title}
                    </Select.Option>
                  ))}
            </Select>
          </Form.Item>
        );
      default:
        break;
    }
  };
  const tabItems = applicationLevels.map(item => ({
    key: String(item.id),
    label: item.title,
    disabled: promptMode === "EDIT",
    children: (
      <Form className="mt-3" form={form} onFinish={handleSave} layout="vertical">
        <div className="flex items-start w-full gap-2">
          <Form.Item
            label={<span className="form-field-label">Prompt usecase</span>}
            className="flex-1"
            name="applicationUseCaseId"
            rules={[{ required: true, message: "Use case is required" }]}
          >
            <Select
              name="applicationUseCaseId"
              disabled={promptMode === "EDIT"}
              placeholder="Select usecase"
              onSelect={value => setformData({ ...formData, applicationUseCaseId: +value })}
              className="w-full"
            >
              {applicationUseCases.map((item, index) => (
                <Select.Option key={index} value={+item.id}>
                  {item.title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <RenderCustomSelect code={formData.levelCode} />
          <Form.Item
            label={<span className="form-field-label">Sources</span>}
            name="leadSourceOriginalIds"
            className="flex-1"
          >
            <Cascader
              multiple
              fieldNames={{ label: "name", value: "key", children: "childs" }}
              expandTrigger="hover"
              options={sourceList}
              maxTagCount="responsive"
              className="w-full"
              value={formData.leadSourceOriginalIds}
              onChange={e => {
                setformData({ ...formData, leadSourceOriginalIds: e });
              }}
              placeholder="Select sources"
            />
          </Form.Item>
        </div>
        {/* <div className="flex flex-row items-center gap-2 mt-3 mb-5">
          <Checkbox>Include Opportunity</Checkbox>
          <Checkbox>Include Campaigns</Checkbox>
          <Checkbox>Include Appointments</Checkbox>
        </div> */}
        <RichTextEditorJSX
          formData={formData}
          className="min-h-[calc(100vh-366px)] p-2 hover:shadow-inner w-full"
          setformData={setformData}
          changingKey="promptText"
        />
      </Form>
    ),
  }));

  return (
    <Tabs
      activeKey={String(formData.applicationLevelId)}
      onChange={key => {
        const selectedLevel = applicationLevels.find(item => String(item.id) === key);
        if (selectedLevel) {
          setformData({
            ...formData,
            applicationLevelId: +selectedLevel.id,
            levelCode: selectedLevel.code,
          });
        }
      }}
      items={tabItems}
      tabBarExtraContent={{
        left: <span className="text-black font-normal text-sm mr-8">Prompt Level:</span>,
      }}
    />
  );
};
